// OrderCheckinDrawer.jsx
import { useEffect, useState, useRef, useMemo, useCallback } from "react";
import {
  FaTimes,
  FaChevronUp,
  FaChevronDown,
  FaPaw,
  FaCheckCircle,
  FaPen,
  FaCalendarCheck,
  FaNotesMedical,
  FaHistory,
  FaEnvelope,
  FaPhone,
  FaMapPin,
  FaConciergeBell,
  FaFileInvoice,
  FaRupeeSign,
  FaRulerCombined,
  FaWeightHanging,
  FaTag,
  FaClock,
  FaChartBar,
  FaReceipt,
  FaCalendar,
  FaCamera,
  FaStickyNote,
} from "react-icons/fa";
import { httpService } from "../../services/httpService";
import LoaderSpiner from "../LoaderSpiner/LoaderSpiner";
import { toast } from "react-toastify";
import {
  GiSittingDog,
  GiCat,
  GiCow,
  GiGoat,
  GiRabbit,
  GiSquirrel,
  GiTortoise,
  GiHummingbird,
} from "react-icons/gi";
import BoardingServiceCheckinCard from "../Checkin/BoardingServiceCheckinCard";
import GroomingServiceCheckinCard from "../Checkin/GroomingServiceCheckinCard";
import VetServiceCheckinCard from "../Checkin/VetServiceCheckinCard";
import { useProfile } from "../../context/ProfileContext";
import AddNewPet from "../../pages/Orders/AddNewPet";
import OrderPaymentDrawer from "../../components/Order/OrderPaymentDrawer";
import Swal from "sweetalert2";
import PetServiceCategoryDrawer from "../../pages/Orders/PetServiceCategoryDrawer";
import PhotoUploader from "../serviceStarted/forms/PhotoUploader";
import OrderFormsSection from "../../components/common/OrderFormsSection";
import BookingDetailsDrawer from "../../pages/Orders/BookingDetailsDrawer";
import AdjustmentAmountCard from "../../components/common/AdjustmentAmountCard";

const getPetIcon = (petType) => {
  switch ((petType || "").toLowerCase()) {
    case "dog": return <GiSittingDog className="w-5 h-5 text-amber-600" />;
    case "cat": return <GiCat className="w-5 h-5 text-gray-600" />;
    case "cow": return <GiCow className="w-5 h-5 text-gray-700" />;
    case "goat": return <GiGoat className="w-5 h-5 text-gray-700" />;
    case "rabbit": return <GiRabbit className="w-5 h-5 text-pink-500" />;
    case "squirrel": return <GiSquirrel className="w-5 h-5 text-orange-600" />;
    case "tortoise":
    case "turtle": return <GiTortoise className="w-5 h-5 text-green-600" />;
    case "bird":
    case "parrot": return <GiHummingbird className="w-5 h-5 text-blue-500" />;
    default: return <GiSittingDog className="w-5 h-5 text-gray-500" />;
  }
};

// ─── Helper: safe numeric coercion for nullable API values ───
const toFiniteNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const normalizeOrderData = (response) => {
  const data = response.data || response;

  const petsMap = {};
  const firstName =
    data.client_first_name &&
      data.client_first_name !== "null" &&
      data.client_first_name !== "undefined"
      ? data.client_first_name.trim()
      : "";

  const lastName =
    data.client_last_name &&
      data.client_last_name !== "null" &&
      data.client_last_name !== "undefined"
      ? data.client_last_name.trim()
      : "";

  const clientName = `${firstName} ${lastName}`.trim();

  data.order_items.forEach((item) => {
    if (!petsMap[item.vendor_pet_id]) {
      petsMap[item.vendor_pet_id] = {
        id: item.vendor_pet_id,
        name: item.pet_name,
        petType: item.pet_type,
        pet_type_id: item.pet_type_id,
        breed: item.pet_breed,
        breed_id: item.pet_breed_id,
        size: item.pet_size,
        size_id: item.pet_size_id,
        weight: item.weight_kg ? `${item.weight_kg} kg` : "-",
        gender: "-",
        dob: "-",
        color: "-",
        tag: item.pet_tags?.length > 0 ? item.pet_tags.join(", ") : "-",
        vaccinated: item.vaccinations?.length > 0,
        vaccinations: item.vaccinations || [],
        medicalCondition: item.medical_condition?.length
          ? item.medical_condition.map((m) => m.condition).join(", ")
          : "None",
        history: [],
        medical:
          item.medical_condition?.map((m) => ({
            text: m.condition,
            date: m.recorded_date,
          })) || [],
        photos: item.photos || [],
        services: [],
        client_id: data.vendor_client_id,
      };
    }

    const finalTotal = Number(item.final_total);
    const additional = Number(item.additional_amount);
    const basePrice =
      Number.isFinite(finalTotal) && Number.isFinite(additional)
        ? Math.max(0, finalTotal - additional)
        : Number(item.total_price) || 0;

    petsMap[item.vendor_pet_id].services.push({
      type: item.item_type,
      serviceName: item.service_name,
      assignedStaff: item.employee_name || "",
      technician_id: item.technician_id || "",
      serviceDate: item.start_date,
      startTime: item.start_time || "",
      duration: item.duration || "",
      checkin: item.start_date,
      checkout: item.end_date,
      roomType: item.room_name,
      roomNumber: item.room_number,
      price: basePrice,
      gst_amount: item?.gst || 0,
      base_total: item?.base_total || 0,
      surchargeDetails: item.price_surcharge || item?.surcharge_details || [],
      discount_amount: item?.discount_amount || 0,
      tax_percentage: item.tax_percentage || 0,
      originalPrice: basePrice,
      notes: item.notes,
      photos: item.photos || item.services_photos || item.service_photos || [],
      grooming_photos: item.photos || item.services_photos || item.service_photos || [],
      services_photos: item.photos || item.services_photos || item.service_photos || [],
      vendor_notes: item.vendor_notes || item.grooming_notes || item.notes || "",
      addons: item.addons || [],
      products: item.products || [],
      otherCharges: item.otherCharges || [],
      service_id: item?.service_id,
      room_type_id: item?.room_type_id || "",
      room_id: item?.room_id || "",
      ref_id: item?.ref_id || "",
      order_id: data?._id || "", 
      item_id: item?.item_id || item?._id || "",
      _id: item?._id || item?.item_id || "",
      vendor_pet_id: item?.vendor_pet_id || "",
      pet_id: item?.pet_id || "",
      form_ids: item?.form_ids || (item?.form_id ? [item.form_id] : []),
      item_type: item?.item_type || "",
      status: item?.status,
      is_queue: item?.is_queue,
    });
  });

  const grandTotal = Number(data.grand_total) || 0;
  const adjustmentAmount = Number(data.adjustment_amount) || 0;
  const amountAfterAdj = Number.isFinite(Number(data.amount_after_adjustment))
    ? Number(data.amount_after_adjustment)
    : grandTotal - adjustmentAmount;

  return {
    orderNumber: data.order_number,
    invoiceNumber: data.order_number
      ? `INV-${data.order_number.replace("ORD-", "")}`
      : "",
    status: data.status,
    client: {
      name: clientName,
      label: "",
      phone: data.client_phone,
      email: data.client_email,
      address: data.client_address,
      overview: "",
      recentVisits: [],
      notes: data.notes || "",
      client_id: data.vendor_client_id,
    },
    orderForms: [],
    documents: [],
    orderNotes: data.notes || "",
    payment: {
      advanceReceived: Number(data.total_paid_amount) || 0,
      grandTotal,                                      // ✅ pre-adjustment total
      total: amountAfterAdj,
      adjustmentAmount,
      adjustmentReason: data.adjustment_reason || "",  // ✅ persisted reason
      amountAfterAdjustment: amountAfterAdj,
      pendingAmount: Math.max(amountAfterAdj - (Number(data.total_paid_amount) || 0), 0),
    },
    pets: Object.values(petsMap),
  };
};

// ─── Helper: ensure date‑only comparison ───
const toDateOnly = (value) => {
  if (!value) return null;
  const d = new Date(value);
  d.setHours(0, 0, 0, 0);
  return d;
};

// ─── Build date-based daily schedule for boarding check-in ───
const buildDailySchedule = (service) => {
  const dateSet = new Set();

  (service.feeding || []).forEach((f) =>
    (f.dates || []).forEach((d) => dateSet.add(d))
  );
  (service.walking || []).forEach((w) =>
    (w.dates || []).forEach((d) => dateSet.add(d))
  );
  (service.medication || []).forEach((m) =>
    (m.dates || []).forEach((d) => dateSet.add(d))
  );

  if (service.checkin && service.checkout) {
    const start = new Date(service.checkin);
    const end = new Date(service.checkout);
    const cur = new Date(start);
    while (cur <= end) {
      const y = cur.getFullYear();
      const m = String(cur.getMonth() + 1).padStart(2, "0");
      const d = String(cur.getDate()).padStart(2, "0");
      dateSet.add(`${y}-${m}-${d}`);
      cur.setDate(cur.getDate() + 1);
    }
  }

  const sortedDates = Array.from(dateSet).sort();

  return sortedDates.map((date) => {
    const feeding_slots = (service.feeding || [])
      .filter((f) => (f.dates || []).includes(date))
      .map((f) => ({
        periods: f.periods || [],
        foodType: f.foodType || "",
        whatToFeed: f.whatToFeed || "",
      }));

    const walking_slots = (service.walking || [])
      .filter((w) => (w.dates || []).includes(date))
      .map((w) => ({
        times: w.times || [],
        duration: Number(w.duration) || 0,
        notes: w.notes || "",
      }));

    const medications = (service.medication || [])
      .filter((m) => (m.dates || []).includes(date))
      .map((m) => ({
        name: m.name || "",
        quantity: m.quantity || "",
        route: m.route || "",
        instructions: m.instructions || "",
        periods: m.periods || [],
      }));

    return { date, feeding_slots, walking_slots, medications };
  });
};

// ---------- Main Component ----------
const OrderCheckinDrawer = ({ isOpen, onClose, orderId, onCheckin }) => {
  const { profileData } = useProfile();
  const vendorId = profileData?._id;
  const systemSettings = profileData?.systemSettings;
  const [order, setOrder] = useState(null);
  const [originalOrderData, setOriginalOrderData] = useState(null);
  const [originalOrder, setOriginalOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activePetId, setActivePetId] = useState("");
  const [activeClientPanel, setActiveClientPanel] = useState(null);
  const [activePetPanel, setActivePetPanel] = useState({});
  const [expandedPets, setExpandedPets] = useState({});
  const [showOrderNotes, setShowOrderNotes] = useState(false);
  const [clientOverviewData, setClientOverviewData] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [showEditPetDrawer, setShowEditPetDrawer] = useState(false);
  const [selectedPet, setSelectedPet] = useState(null);
  const [showPaymentDrawer, setShowPaymentDrawer] = useState(false);

  const [isSaving, setIsSaving] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);

  const [recentVisits, setRecentVisits] = useState([]);
  const [expandedVisit, setExpandedVisit] = useState(null);
  const [invoiceLoading, setInvoiceLoading] = useState(false);

  const [rescheduleDrawer, setRescheduleDrawer] = useState({
    isOpen: false,
    appointment: null,
  });

  const [checkingInPetId, setCheckingInPetId] = useState(null);

  // ---------- Adjustment state ----------
  const [adjustmentAmount, setAdjustmentAmount] = useState(0);
  const [adjustmentReason, setAdjustmentReason] = useState("");
  const [savingAdjustment, setSavingAdjustment] = useState(false);

  // Sync adjustment inputs whenever the order reloads / recalculates
  useEffect(() => {
    if (order?.payment) {
      setAdjustmentAmount(Number(order.payment.adjustmentAmount) || 0);
      setAdjustmentReason(order.payment.adjustmentReason || "");
    }
  }, [order?.payment?.adjustmentAmount, order?.payment?.adjustmentReason]);

  const handleSaveAdjustment = async () => {
    if (!orderId || savingAdjustment) return;

    const numericAmount = parseFloat(adjustmentAmount) || 0;
    const originalAmount = parseFloat(order?.payment?.grandTotal || 0);
    const currentAdjustment = parseFloat(order?.payment?.adjustmentAmount || 0);
    const currentReason = order?.payment?.adjustmentReason || "";

    if (numericAmount < 0) {
      toast.error("Adjustment cannot be negative");
      return;
    }
    if (numericAmount > originalAmount) {
      toast.error(
        `Adjustment cannot exceed order amount (₹${originalAmount.toFixed(2)})`
      );
      return;
    }
    // No-op if nothing changed
    if (numericAmount === currentAdjustment && adjustmentReason === currentReason) {
      return;
    }

    try {
      setSavingAdjustment(true);
      const res = await httpService.post("/orderpayment/saveOrderAdjustment", {
        order_id: orderId,
        adjustment_amount: numericAmount,
        adjustment_reason: adjustmentReason,
      });

      if (res?.status) {
        toast.success("Adjustment saved successfully!", { position: "top-center" });
        await fetchOrder(); // refresh totals + payment block
      } else {
        throw new Error(res?.message || "Failed to save adjustment");
      }
    } catch (err) {
      console.error("Save adjustment error:", err);
      toast.error(
        err?.response?.data?.message || "Failed to save adjustment",
        { position: "top-center" }
      );
      // revert inputs to last known
      setAdjustmentAmount(currentAdjustment);
      setAdjustmentReason(currentReason);
    } finally {
      setSavingAdjustment(false);
    }
  };

  // ---------- Recalculation state & refs ----------
  const [isRecalculating, setIsRecalculating] = useState(false);
  const recalcTimer = useRef(null);
  const isInitialRecalcMount = useRef(true);

  // ---------- Recalculate order totals ----------
  const recalculateOrder = useCallback(async () => {
    if (!order || !order.pets.length || !order.client?.client_id) return;

    const items = [];
    const includedServices = [];

    for (const pet of order.pets) {
      for (let i = 0; i < pet.services.length; i++) {
        const service = pet.services[i];

        if (service.status === "cancelled") continue;

        const isBoarding =
          service.type === "boarding" || service.type === "day_care";
        let quantity = service.quantity || 1;
        if (isBoarding && service.checkin && service.checkout) {
          const nights = Math.max(
            1,
            Math.ceil(
              (new Date(service.checkout) - new Date(service.checkin)) / 86400000
            )
          );
          quantity = nights;
        }

        const info = {
          pet_details: {
            _id: pet.id,
            breed_info: { _id: pet.breed_id },
            size_info: { _id: pet.size_id },
          },
          vendor_service_id: service.serviceId || service.service_id,
          room_service_id: service.serviceId || service.service_id,
          room_type_id: service.room_type_id || null,
          start_date: service.checkin,
          end_date: service.checkout || null,
        };

        if (!isBoarding) {
          info.appointment_date = service.checkin;
        }

        items.push({
          type: service.type,
          quantity,
          [isBoarding ? "boarding_info" : "appointment_info"]: info,
          products: service.products || [],
          addons: service.addons || [],
          otherCharges: service.otherCharges || [],
        });

        includedServices.push({ petId: pet.id, serviceIndex: i });
      }
    }

    if (items.length === 0) return;

    const payload = {
      vendor_client_id: order.client.client_id,
      items,
      round_off: 0,
      waived_amount: 0,
    };

    setIsRecalculating(true);
    try {
      const response = await httpService.post("/vendororder/recalculateOrder", payload);
      const data = response.data;

      const grandTotal = toFiniteNumber(data.grand_total) ?? 0;

      const adjustmentAmount =
        toFiniteNumber(data.adjustment_amount) ??
        toFiniteNumber(order.payment?.adjustmentAmount) ??
        0;

      const amountAfterAdj =
        toFiniteNumber(data.amount_after_adjustment) ??
        grandTotal - adjustmentAmount;

      const advanceReceived = Number(order.payment.advanceReceived) || 0;

      let hasChanges = false;

      if (Math.abs(amountAfterAdj - (order.payment.total || 0)) > 0.01) {
        hasChanges = true;
      }

      if (!hasChanges) {
        for (let idx = 0; idx < includedServices.length; idx++) {
          const { petId, serviceIndex } = includedServices[idx];
          const pet = order.pets.find((p) => p.id === petId);
          if (!pet) continue;
          const service = pet.services[serviceIndex];
          if (!service) continue;
          const calculated = data.items?.[idx];
          if (
            calculated &&
            Math.abs((calculated.total_price || 0) - service.price) > 0.01
          ) {
            hasChanges = true;
            break;
          }
        }
      }

      if (!hasChanges) return;

      setOrder((prevOrder) => {
        const updatedOrder = { ...prevOrder };
        updatedOrder.payment = {
          ...prevOrder.payment,
          grandTotal,                                     // ✅ keep in sync
          total: amountAfterAdj,
          adjustmentAmount,
          amountAfterAdjustment: amountAfterAdj,
          pendingAmount: Math.max(amountAfterAdj - advanceReceived, 0),
        };

        for (let idx = 0; idx < includedServices.length; idx++) {
          const { petId, serviceIndex } = includedServices[idx];
          const calculated = data.items?.[idx];
          if (!calculated) continue;

          const pet = updatedOrder.pets.find((p) => p.id === petId);
          if (!pet) continue;
          const service = pet.services[serviceIndex];
          if (!service) continue;

          const addonsTotal =
            (service.addons || []).reduce((s, x) => s + (Number(x.price) || 0), 0) +
            (service.products || []).reduce((s, x) => s + (Number(x.price) || 0), 0) +
            (service.otherCharges || []).reduce((s, x) => s + (Number(x.price) || 0), 0);

          const newBasePrice =
            (calculated.total_price ?? service.price);

          service.price = Math.max(0, newBasePrice);
          service.unit_price = calculated.unit_price ?? service.unit_price;
          service.tax_percentage = calculated.tax_percentage ?? service.tax_percentage;
          service.gst_amount = calculated.gst ?? service.gst_amount;
          service.discount_amount = calculated.discount_amount ?? service.discount_amount;

          const newSurcharges = calculated.surcharge_details || [];
          service.surchargeDetails = newSurcharges;
          service.price_surcharge = newSurcharges;
          service.surchargesTotal = newSurcharges.reduce(
            (sum, s) => sum + (Number(s.price) || 0),
            0
          );
        }
        return updatedOrder;
      });
    } catch (error) {
      console.error("Recalculation failed:", error);
    } finally {
      setIsRecalculating(false);
    }
  }, [order]);

  const recalcDependencyKey = useMemo(() => {
    if (!order || !order.pets) return "";
    return JSON.stringify(
      order.pets.map((pet) => ({
        id: pet.id,
        services: pet.services.map((s) => ({
          type: s.type,
          checkin: s.checkin,
          checkout: s.checkout,
          service_id: s.serviceId,
          room_type_id: s.room_type_id,
          products: s.products,
          addons: s.addons,
          otherCharges: s.otherCharges,
        })),
      }))
    );
  }, [order]);

  useEffect(() => {
    if (isInitialRecalcMount.current) {
      isInitialRecalcMount.current = false;
      return;
    }
    if (loading) return;

    if (recalcTimer.current) clearTimeout(recalcTimer.current);

    if (!order || !order.pets.length || !order.client?.client_id) return;

    recalcTimer.current = setTimeout(() => {
      recalculateOrder();
    }, 500);

    return () => {
      if (recalcTimer.current) clearTimeout(recalcTimer.current);
    };
  }, [recalcDependencyKey]);

  const [showBookingDetails, setShowBookingDetails] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState(null);

  const handleCloseBookingDetails = () => {
    setShowBookingDetails(false);
    setSelectedOrderId(null);
    fetchOrder();
  };

  const openBookingDetails = () => {
    try {
      if (orderId) {
        setSelectedOrderId(orderId);
        setShowBookingDetails(true);
      } else {
        Swal.fire("Error", "Unable to open appointment - missing order ID", "error");
      }
    } catch (err) {
      console.error("Error opening appointment details:", err);
    }
  };

  // ─── Photo handling per pet ───
  const handlePetPhotosChange = (petId, newPhotos) => {
    setOrder((prev) => {
      if (!prev) return prev;
      const updatedPets = prev.pets.map((pet) =>
        pet.id !== petId ? pet : { ...pet, photos: newPhotos }
      );
      return { ...prev, pets: updatedPets };
    });
  };

  const handlePetNotesChange = (petId, newNotes) => {
    setOrder((prev) => {
      if (!prev) return prev;
      const updatedPets = prev.pets.map((pet) =>
        pet.id !== petId ? pet : { ...pet, notes: newNotes }
      );
      return { ...prev, pets: updatedPets };
    });
  };

  // ─── Pricing‑only change detection ───
  const getPricingSnapshot = (pets) => {
    return pets.map((pet) =>
      pet.services.map((s) => ({
        price: s.price,
        addons: s.addons || [],
        products: s.products || [],
        otherCharges: s.otherCharges || [],
        startTime: s.startTime,
        serviceDate: s.serviceDate,
        technician_id: s.technician_id,
        serviceId: s.serviceId || s.service_id,
      }))
    );
  };

  const hasPricingChanges = useMemo(() => {
    if (!order || !originalOrder) return false;
    const current = getPricingSnapshot(order.pets);
    const original = getPricingSnapshot(originalOrder.pets);
    return JSON.stringify(current) !== JSON.stringify(original);
  }, [order, originalOrder]);

  const getSinglePetPricingSnapshot = (pet) => {
    if (!pet) return null;
    return pet.services.map((s) => ({
      price: s.price,
      addons: s.addons || [],
      products: s.products || [],
      otherCharges: s.otherCharges || [],
      startTime: s.startTime,
      serviceDate: s.serviceDate,
      technician_id: s.technician_id,
      serviceId: s.serviceId || s.service_id,
    }));
  };

  const hasPetPricingChanges = (petId) => {
    if (!order || !originalOrder) return false;
    const currentPet = order.pets.find((p) => p.id === petId);
    const originalPet = originalOrder.pets.find((p) => p.id === petId);
    if (!currentPet || !originalPet) return false;
    return (
      JSON.stringify(getSinglePetPricingSnapshot(currentPet)) !==
      JSON.stringify(getSinglePetPricingSnapshot(originalPet))
    );
  };

  // ─── Boarding range & editability helpers ───
  const getBoardingRangeForPet = (pet) => {
    if (!pet?.services) return null;
    const boarding = pet.services.find((s) => s.type === "boarding");
    if (!boarding || !boarding.checkin || !boarding.checkout) return null;
    return {
      start: toDateOnly(boarding.checkin),
      end: toDateOnly(boarding.checkout),
    };
  };

  const isServiceEditable = (pet, service) => {
    if (
      service.status === "cancelled" ||
      service.status === "completed" ||
      service.status === "no_show" ||
      service.status === "started"
    )
      return false;
    const today = toDateOnly(new Date());

    if (service.type === "boarding") {
      const checkinDate = toDateOnly(service.checkin);
      return checkinDate <= today;
    }

    const range = getBoardingRangeForPet(pet);
    const serviceDate = toDateOnly(service.serviceDate);

    if (!range) {
      return serviceDate <= today;
    }

    if (range.start > today) {
      return serviceDate <= today;
    }

    return serviceDate >= range.start && serviceDate <= range.end;
  };

  // ─── Boarding configuration validation ───
  const isBoardingServiceConfigured = (service) => {
    if (service.type !== "boarding" && service.type !== "day_care") return true;
    const svcId = service.serviceId || service.service_id || "";
    const roomTypeId = service.room_type_id || service.roomTypeId || "";
    return Boolean(svcId && roomTypeId);
  };

  const isPetConfigured = (pet) => {
    if (!pet?.services?.length) return true;
    return pet.services.every(isBoardingServiceConfigured);
  };

  // ─── Check-in eligibility helper ───
  const canShowCheckin = (pet) => {
    if (!isPetConfigured(pet)) return false;

    return pet.services.some(
      (service) =>
        service.status === "confirmed" &&
        service.is_queue === 0 &&
        isServiceEditable(pet, service)
    );
  };

  // ---------- Fetch helpers ----------
  const fetchRecentVisits = async (clientId) => {
    if (!clientId) return;
    try {
      const res = await httpService.get(`/vendorbooking/bookingHistory/${clientId}`);
      if (res.status && Array.isArray(res.data)) {
        setRecentVisits(res.data);
      } else {
        setRecentVisits([]);
      }
    } catch (err) {
      console.error(err);
      setRecentVisits([]);
    }
  };

  const visitCards = recentVisits.flatMap((order) =>
    (order.order_items || []).map((item) => ({
      orderId: order._id,
      orderNumber: order.order_number,
      createdAt: order.createdAt,
      paymentStatus: order.payment_status,
      ...item,
    }))
  );

  const getPetVisitCards = (petId) => {
    return recentVisits.flatMap((order) =>
      (order.order_items || [])
        .filter((item) => item.vendor_pet_id === petId)
        .map((item) => ({
          orderId: order._id,
          orderNumber: order.order_number,
          createdAt: order.createdAt,
          paymentStatus: order.payment_status,
          ...item,
        }))
    );
  };

  const isProductOnlyOrder = useMemo(() => {
    if (!order) return false;
    return !order.pets?.length || order.pets.every((p) => !p.services?.length);
  }, [order]);

  const mapPrinterToUrlValue = (printer) => {
    if (!printer || typeof printer !== "string") return null;
    const p = printer.toLowerCase().trim();
    if (p === "thermal") return "thermal";
    if (["dotmatrix", "dot_matrix", "dot-matrix", "dot matrix"].includes(p)) return "dotmatrix";
    if (["laser", "inkjet", "ink", "ink jet", "ink-jet"].includes(p)) return "laser";
     if (
       ["letterhead", "letter_head", "letter-head", "letter head"].includes(p)
     )
       return "letterhead"; 
    return null;
  };

    const handleViewInvoice = async () => {
      try {
        let printerItems = systemSettings?.printer_by_item?.items || [];

        try {
          const fresh = await httpService.get(
            `/vendors/getOnlineProfile?vendorId=${vendorId}`,
          );
          printerItems =
            fresh?.data?.systemSettings?.printer_by_item?.items || printerItems;
        } catch (e) {
          console.error("Failed to refresh printer settings:", e);
        }
        const targetName = isProductOnlyOrder
          ? "Invoice – POS"
          : "Invoice – Service";

        const invoiceConfig = printerItems.find(
          (item) =>
            item.name === targetName ||
            (item.name || "").toLowerCase() === targetName.toLowerCase(),
        );

        const selectedPrinter = invoiceConfig?.printer;
        const printerValue = mapPrinterToUrlValue(selectedPrinter);

        if (printerValue) {
          const baseUrl =
            process.env.REACT_APP_ADMIN_URL || "http://localhost:3000";
          const previewUrl = `${baseUrl}/admin/invoicePreview/${printerValue}/${orderId}`;
          window.open(previewUrl, "_blank", "noopener,noreferrer");
          return;
        }

        if (!orderId || !vendorId) return;

        setInvoiceLoading(true);
        const response = await httpService.get(
          `/invoice/getorderinvoice?orderId=${orderId}&vendorId=${vendorId}`,
        );

        if (response.status && response.data?.invoice_url) {
          window.open(response.data.invoice_url, "_blank");
        } else {
          toast.error("Invoice not found");
        }
      } catch (err) {
        console.error("Failed fetching invoice", err);
        toast.error("Failed to fetch invoice");
      } finally {
        setInvoiceLoading(false);
      }
    };

  const fetchClientOverview = async () => {
    if (!order?.client?.client_id) return;
    try {
      setLoadingStats(true);
      const response = await httpService.get(
        `/vendorclient/getVendorClientPetsOverviewById/${order.client.client_id}`
      );
      if (response.code === 200) {
        setClientOverviewData(response.data?.data || response.data);
      }
    } catch (error) {
      console.error("Error fetching client overview:", error);
    } finally {
      setLoadingStats(false);
    }
  };

  useEffect(() => {
    if (isOpen && order?.client?.client_id) {
      fetchClientOverview();
    }
  }, [isOpen, order?.client?.client_id]);

  const calculateStats = () => {
    if (!clientOverviewData) {
      return { paidAmount: 0, pendingAmount: 0, totalOrders: 0, cancellations: 0 };
    }

    const overviewData = clientOverviewData?.data || clientOverviewData;
    const paymentDetails = overviewData.payment_details || {};

    const paidAmount =
      paymentDetails.received?.reduce(
        (sum, payment) => sum + (parseFloat(payment.payment_amount) || 0),
        0
      ) || 0;

    const pendingRaw = paymentDetails.pending || [];
    const received = paymentDetails.received || [];
    const filteredPending = pendingRaw.filter((pending) => {
      const orderTotal = pending.order_amount;
      const receivedForOrder = received.filter(
        (p) =>
          p.order_number === pending.order_number || p.order_id === pending.order_id
      );
      const totalReceived = receivedForOrder.reduce(
        (sum, p) => sum + (p.payment_amount || 0),
        0
      );
      return totalReceived < orderTotal - 0.01;
    });
    const pendingAmount = filteredPending.reduce((sum, p) => sum + (p.balance || 0), 0);

    const totalOrders = overviewData.order_details?.length || 0;

    let totalCancellations = 0;
    totalCancellations +=
      overviewData.order_details?.filter((order) => {
        const status = order.status?.toLowerCase();
        return status === "cancel" || status === "cancelled";
      }).length || 0;

    totalCancellations +=
      overviewData.upcoming_appointment_details?.filter((appointment) => {
        const appointmentStatus = appointment.status?.toLowerCase();
        if (appointmentStatus === "cancel" || appointmentStatus === "cancelled") return true;
        return appointment.items?.some((item) => {
          const serviceInfo = item.appointment_info || item.boarding_info;
          return ["cancel", "cancelled"].includes(serviceInfo?.status?.toLowerCase());
        });
      }).length || 0;

    totalCancellations +=
      overviewData.completed_cancelled_appointments?.filter((appointment) => {
        const appointmentStatus = appointment.status?.toLowerCase();
        if (appointmentStatus === "cancel" || appointmentStatus === "cancelled") return true;
        return appointment.items?.some((item) => {
          const serviceInfo = item.appointment_info || item.boarding_info;
          const itemStatus = serviceInfo?.status?.toLowerCase();
          return ["cancel", "cancelled"].includes(itemStatus);
        });
      }).length || 0;

    return { paidAmount, pendingAmount, totalOrders, cancellations: totalCancellations };
  };

  const fetchOrder = async () => {
    try {
      setLoading(true);
      const res = await httpService.get(`/vendororder/getvendororder/${orderId}`);
      if (res.code === 200) {
        setOriginalOrderData(res.data);
        const normalized = normalizeOrderData(res.data);
        setOrder(normalized);
        setOriginalOrder(structuredClone(normalized));
        fetchRecentVisits(res.data.vendor_client_id);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen || !orderId) return;
    fetchOrder();
  }, [isOpen, orderId]);

  useEffect(() => {
    if (!order?.pets?.length) return;
    if (!order.pets.find((p) => p.id === activePetId)) {
      setActivePetId(order.pets[0].id);
    }
  }, [order, activePetId]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      document.documentElement.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
    };
  }, [isOpen]);

  const toggleClientPanel = (panel) => {
    setActiveClientPanel((prev) => (prev === panel ? null : panel));
  };

  const togglePetPanel = (petId, panel) => {
    setActivePetPanel((prev) => ({
      ...prev,
      [petId]: prev[petId] === panel ? null : panel,
    }));
  };

  const togglePetAccordion = (petId) => {
    setExpandedPets((prev) => ({ ...prev, [petId]: !prev[petId] }));
  };

  const handleEditPet = (petId) => {
    const pet = order.pets.find((p) => p.id === petId);
    if (!pet) return;
    setSelectedPet(pet);
    setShowEditPetDrawer(true);
  };

  const handleCancelservice = async (service) => {
    const result = await Swal.fire({
      title: "Cancel Booking?",
      text: "This will cancel the Booking in this order. Continue?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, Cancel",
      cancelButtonText: "No",
    });
    if (!result.isConfirmed) return;

    setIsCancelling(true);
    try {
      if (service.type === "boarding") {
        await httpService.put(
          `/vendorbooking/updateVendorBookingStatus/${service.ref_id}`,
          { status: "cancelled" }
        );
      } else {
        await httpService.put(
          `/vendorappointment/updateVendorAppointmentStatus/${service.ref_id}`,
          { status: "cancelled" }
        );
      }
      toast.success("Booking cancelled successfully.");
      fetchOrder();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Failed to cancel order.");
    } finally {
      setIsCancelling(false);
    }
  };

  const handleServiceModify = async (booking, pet) => {
    try {
      const originalOrderItem = originalOrderData?.order_items?.find(
        (item) => item.ref_id === booking.ref_id
      );

      if (!originalOrderItem) {
        toast.error("Could not find original order item for editing");
        return;
      }

      const itemType = originalOrderItem.item_type?.toLowerCase();
      let response;

      if (itemType === "boarding" || itemType === "day_care") {
        response = await httpService.get(`/vendorbooking/getVendorBooking/${booking.ref_id}`);
      } else {
        response = await httpService.get(`/vendorappointment/getVendorAppointment/${booking.ref_id}`);
      }

      if (response?.data) {
        const editData = {
          ...response.data,
          pet_id: booking.pet_id || response.data.vendor_pet_id,
          vendor_pet_id: booking.pet_id || response.data.vendor_pet_id,
          service_id: booking.service_id || response.data.service_id,
          vendor_client_id: order.client.client_id,
          service_category_type_id:
            response.data.service_category_type_id ||
            booking.service_category_type_id ||
            originalOrderItem.service_category_type_id,
          service_type_name:
            response.data.service_type_name ||
            booking.service_type_name ||
            booking.service_name ||
            originalOrderItem.service_type_name,
          service_name:
            response.data.service_name ||
            booking.service_name ||
            booking.service_type_name ||
            originalOrderItem.service_name,
          room_type_id:
            originalOrderItem.room_type_id ||
            response.data.room_type_id ||
            booking.room_type_id,
          employee_id: booking.technician_id,
          employee_name: booking.employee_name,
          start_date: booking.start_date || originalOrderItem.start_date,
          end_date: booking.end_date || originalOrderItem.end_date,
          start_time: booking.start_time || originalOrderItem.start_time,
          order_id: orderId,
          item_type: originalOrderItem.item_type,
          notes: response.data.notes || booking.notes || originalOrderItem.notes || "",
          frequency:
            response.data.frequency ||
            booking.frequency ||
            originalOrderItem.frequency ||
            "",
          numberOfTimes: booking.numberOfTimes || originalOrderItem.number_of_times || "",
          ref_id: booking.ref_id || "",
          duration: response.data.duration || booking.duration || "",
          price: response.data.price || booking.price || booking.unit_price || 0,
          discount: response.data.discount || booking.discount || 0,
          gst: response.data.gst || booking.gst || 0,
          total_price: response.data.total_price || booking.total_price || 0,
          quantity: response.data.quantity || booking.quantity || 1,
          addons: response.data.addons || booking.addons || [],
          service_code: response.data.service_code || "",
          client_name: response.data.client_name || "",
          pet_name: response.data.pet_name || "",
          type: response.data.type || originalOrderItem.item_type || "day_care",
          vendor_pet_id: pet.id,
        };

        if (originalOrderItem.item_type === "dog_walking") {
          editData.service_category_type_id = 1;
          editData.service_type_name = "Dog walking";
        }

        setRescheduleDrawer({ isOpen: true, appointment: editData });
      }
    } catch (error) {
      console.error("Error fetching appointment/booking details:", error);
    }
  };

  // ─── Build order payload for saving ───
  const buildOrderPayload = () => {
    if (!order || !order.pets.length || !order.client?.client_id) return null;

    const items = [];

    order.pets.forEach((pet) => {
      pet.services.forEach((service, idx) => {
        const item = {
          item_type: service.type,
          vendor_pet_id: pet.id,
          pet_name: pet.name,
          pet_type: pet.petType,
          pet_type_id: pet.pet_type_id,
          pet_size: pet.size,
          pet_breed: pet.breed,
          service_id: service.serviceId || service.service_id,
          service_name: service.serviceName || "",
          technician_id: service.technician_id || "",
          employee_name: service.assignedStaff || "",
          start_date: service.checkin || service.serviceDate,
          start_time: service.startTime,
          duration: service.duration,
          unit_price: service.unit_price ?? service.price,
          price: service.price,
          tax_percentage: service.tax_percentage ?? 0,
          gst: service.gst_amount ?? 0,
          discounted_tax: service.gst_amount ?? 0,
          total_price: service.price,
          base_total: service?.base_total || service?.price || 0,
          sale_price: service.sale_price ?? service.price,
          notes: service.vendor_notes || service.grooming_notes || service.notes,
          photos: service.photos || service.grooming_photos || [],
          services_photos: service.photos || service.grooming_photos || [],
          addons: service.addons || [],
          products: service.products || [],
          otherCharges: service.otherCharges || [],
          price_surcharge: service.price_surcharge || service.surchargeDetails || service.surcharge_details || [],
          surchargeDetails: service.surcharge_details || service.surchargeDetails || service.price_surcharge || [],
          surchargesTotal: service.surchargesTotal ?? 0,
          form_ids: service.form_ids || [],
          ref_id: service.ref_id,
          status: service.status,
          quantity: service.quantity || 1,
        };

        if (service.type === "boarding" || service.type === "day_care") {
          item.end_date = service.checkout;
          item.room_type_id = service.room_type_id;
          item.room_id = service.room_id;
          item.room_number = service.roomNumber;
          item.room_type_name = service.roomType;
          item.price_per_day = service.price_per_day || service.unit_price;
          const duration =
            service.checkout && service.checkin
              ? Math.max(
                  1,
                  Math.ceil(
                    (new Date(service.checkout) - new Date(service.checkin)) / 86400000
                  )
                )
              : 1;
          item.duration = duration;
          item.quantity = duration;
        } else if (service.type === "grooming") {
          item.service_category_type_id = service.service_category_type_id || "";
        } else if (service.type === "vet" || service.type === "vet_clinic") {
          item.vet_details = service.vet_details || {};
          item.service_category_name =
            service.service_category_name || "Appointment Category";
        }

        item._editIndex = idx;

        items.push(item);
      });
    });

    return {
      order_type: order.order_type,
      vendor_client_id: order.client.client_id,
      order_items: items,
      order_id: orderId,
    };
  };

  // ─── Check‑in logic ───
  const handleCheckin = async (pet) => {
    if (checkingInPetId) return;

    // ✅ Guard: require all boarding services to have service + room type
    if (!isPetConfigured(pet)) {
      toast.error(
        "Please select a service and room type for all boarding services before checking in."
      );
      return;
    }

    setCheckingInPetId(pet.id);

    const eligibleServices = pet.services.filter(
      (service) =>
        service.status === "confirmed" &&
        service.is_queue === 0 &&
        isServiceEditable(pet, service) &&
        !["checked_in", "in_progress", "completed", "cancelled", "started"].includes(
          service.status
        )
    );

    if (eligibleServices.length === 0) {
      toast.error("No eligible services to check in.");
      setCheckingInPetId(null);
      return;
    }

    try {
      const savePayload = buildOrderPayload();
      let addVendorOrderOrUpdateReponse;
      if (savePayload) {
        addVendorOrderOrUpdateReponse = await httpService.post(
          "/vendororder/addVendorOrderOrUpdate",
          savePayload
        );
      }

      for (const service of eligibleServices) {
        let checkinData;
        if (service.type === "boarding") {
          checkinData = {
            daily_schedule: buildDailySchedule(service),
            belongings: service.belongings || "",
            notes: pet.notes || "",
            photos: pet.photos || [],
          };
        } else {
          const servicePhotos =
            (Array.isArray(service.photos) && service.photos.length ? service.photos : null) ||
            (Array.isArray(service.grooming_photos) && service.grooming_photos.length
              ? service.grooming_photos
              : null) ||
            (Array.isArray(service.services_photos) && service.services_photos.length
              ? service.services_photos
              : null) ||
            (Array.isArray(pet.photos) && pet.photos.length ? pet.photos : []);

          checkinData = {
            photos: servicePhotos,
            service_photos: servicePhotos,
            services_photos: servicePhotos,
            notes: service.vendor_notes || service.grooming_notes || pet.notes || "",
            ...(service.type === "vet_clinic" || service.type === "vet"
              ? { vitals: service.vitals || [] }
              : {}),
          };
        }

        await httpService.post("/checkin", {
          service_type: service.type,
          service_ref_id: service.ref_id,
          pet_id: pet.id,
          checkin_data: checkinData,
        });

        const targetClientId = pet.client_id || order?.client?.client_id;
        const servicePhotos = checkinData.photos || [];
        const serviceNotes = checkinData.notes || "";

        if (targetClientId) {
          if (servicePhotos.length > 0) {
            await httpService
              .post("/notes/updatenote", {
                type: "photo",
                noteText: "",
                vendor_id: vendorId,
                vendor_client_id: targetClientId,
                photos: servicePhotos,
              })
              .catch((e) => console.error("Error saving grooming photo note:", e));
          }

          if (serviceNotes && serviceNotes.trim().length > 0) {
            await httpService
              .post("/notes/updatenote", {
                type: "note",
                noteText: serviceNotes.trim(),
                vendor_id: vendorId,
                vendor_client_id: targetClientId,
                photos: [],
              })
              .catch((e) => console.error("Error saving grooming note:", e));
          }
        }
      }

      toast.success("All eligible services checked in");

      if (eligibleServices.length > 0 && onCheckin) {
        const first = eligibleServices[0];
        await onCheckin(first.ref_id, first.type, orderId);
      }
      onClose();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Check‑in failed");
    } finally {
      setCheckingInPetId(null);
    }
  };

  const handleCancel = async () => {
    const result = await Swal.fire({
      title: "Cancel Order?",
      text: "This will cancel the entire order. Continue?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, Cancel All",
      cancelButtonText: "No",
    });
    if (!result.isConfirmed) return;

    setIsCancelling(true);
    try {
      const cancelOrderId = orderId || originalOrderData?._id;

      if (!cancelOrderId) {
        toast.error("Order ID is missing. Unable to cancel order.");
        return;
      }
      await httpService.post("/vendororder/cancelOrder", { order_id: cancelOrderId });

      toast.success("Order cancelled successfully.");
      setTimeout(() => { onClose(); }, 1200);
    } catch (err) {
      console.error("Error cancelling order:", err);
      toast.error(err?.response?.data?.message || "Failed to cancel order.");
    } finally {
      setIsCancelling(false);
    }
  };

  const handleSave = async () => {
    const payload = buildOrderPayload();
    if (!payload) {
      toast.error("Nothing to save");
      return;
    }

    setIsSaving(true);
    try {
      const res = await httpService.post("/vendororder/addVendorOrderOrUpdate", payload);
      if (res.code === 200 || res.status) {
        toast.success("Order updated successfully");

        const fresh = await httpService.get(`/vendororder/getvendororder/${orderId}`);
        if (fresh.code === 200) {
          const freshOrder = normalizeOrderData(fresh.data);

          if (order?.pets) {
            freshOrder.pets = freshOrder.pets.map((newPet) => {
              const oldPet = order.pets.find((p) => p.id === newPet.id);
              if (!oldPet) return newPet;

              newPet.photos = oldPet.photos || [];
              newPet.notes = oldPet.notes || "";

              newPet.services = newPet.services.map((newService, idx) => {
                const oldService = oldPet.services[idx];
                if (!oldService) return newService;

                return {
                  ...newService,
                  vitals: oldService.vitals ?? newService.vitals,
                  feeding: oldService.feeding ?? newService.feeding,
                  walking: oldService.walking ?? newService.walking,
                  medication: oldService.medication ?? newService.medication,
                  belongings: oldService.belongings ?? newService.belongings,
                  photos: oldService.photos ?? newService.photos,
                  grooming_photos: oldService.grooming_photos ?? newService.grooming_photos,
                  vendor_notes: oldService.vendor_notes ?? newService.vendor_notes,
                  grooming_notes: oldService.grooming_notes ?? newService.grooming_notes,
                };
              });

              return newPet;
            });
          }

          setOrder(freshOrder);
          setOriginalOrder(structuredClone(freshOrder));
        }
      } else {
        throw new Error(res.message || "Update failed");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to save changes");
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdateService = (petId, serviceIndex, updatedService) => {
    setOrder((prev) => {
      const pets = prev.pets.map((pet) => {
        if (pet.id !== petId) return pet;
        const services = [...pet.services];
        services[serviceIndex] = updatedService;
        return { ...pet, services };
      });
      return { ...prev, pets };
    });
  };

  const getOriginalService = (petId, serviceIndex) => {
    if (!originalOrder) return null;
    const originalPet = originalOrder.pets.find((p) => p.id === petId);
    return originalPet?.services[serviceIndex] ?? null;
  };

  const resetService = (petId, serviceIndex) => {
    const originalService = getOriginalService(petId, serviceIndex);
    if (!originalService) return;
    handleUpdateService(petId, serviceIndex, structuredClone(originalService));
  };

  // ---------- Render helpers ----------
  const renderPetHeader = (pet) => {
    const isExpanded = expandedPets[pet.id] ?? order.pets.length === 1;
    const showCheckin = canShowCheckin(pet);
    const petHasChanges = hasPetPricingChanges(pet.id);
    const petConfigured = isPetConfigured(pet);   // ✅ NEW

    return (
      <div
        className="flex items-center justify-between px-5 py-3 bg-gray-50 hover:bg-gray-100 transition cursor-pointer border-b border-gray-200"
        onClick={() => togglePetAccordion(pet.id)}
      >
        <div className="flex items-center gap-3">
          {getPetIcon(pet.petType)}
          <span className="font-semibold text-gray-800">{pet.name}</span>
          <span className="text-xs bg-gray-200 text-gray-600 rounded-full px-2 py-0.5">
            {pet.services.length}{" "}
            {pet.services.length === 1 ? "service" : "services"}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => { e.stopPropagation(); handleCancel(); }}
            disabled={isCancelling}
            className="text-sm font-semibold flex items-center gap-2 bg-red-500 text-white px-3 py-1.5 rounded-full hover:bg-red-700 transition disabled:opacity-50"
          >
            {isCancelling ? "Cancelling..." : "Cancel Order"}
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); handleEditPet(pet.id); }}
            className="text-sm font-semibold text-indigo-600 bg-indigo-50 px-3 py-1.5 rounded-full hover:bg-indigo-100 transition flex items-center gap-1"
          >
            <FaPen size={12} /> Edit Pet
          </button>

          {/* 🔒 Save / Check In only shown when service + room type are set */}
          {!petConfigured ? (
            <span className="text-xs font-medium text-amber-700 bg-amber-50 px-3 py-1.5 rounded-full">
              Select service &amp; room type
            </span>
          ) : petHasChanges ? (
            <button
              onClick={handleSave}
              className="inline-flex items-center gap-2 bg-gray-800 text-white px-3 py-1 rounded-xl font-semibold hover:bg-gray-900 transition disabled:opacity-50"
            >
              <FaCheckCircle /> Save
            </button>
          ) : showCheckin ? (
            <button
              onClick={(e) => { e.stopPropagation(); handleCheckin(pet); }}
              disabled={checkingInPetId === pet.id}
              className="text-sm font-semibold text-white bg-indigo-600 px-3 py-1.5 rounded-full hover:bg-indigo-700 transition flex items-center gap-1"
            >
              {checkingInPetId === pet.id ? (
                "Checking..."
              ) : (
                <><FaCheckCircle size={14} /> Check In</>
              )}
            </button>
          ) : null}

          {isExpanded ? (
            <FaChevronUp className="text-gray-500 ml-1" />
          ) : (
            <FaChevronDown className="text-gray-500 ml-1" />
          )}
        </div>
      </div>
    );
  };

  const renderPetDetails = (pet) => (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm border-b border-gray-100 pb-2 mb-2">
      <span className="flex items-center gap-1">
        <FaPaw className="text-gray-500" />{" "}
        <span className="font-medium">{pet.breed}</span>
      </span>
      <span className="text-gray-300">|</span>
      <span className="flex items-center gap-1">
        <FaRulerCombined className="text-gray-500" />{" "}
        <span className="font-medium">{pet.size}</span>
      </span>
      {pet.weight && pet.weight !== "-" && (
        <>
          <span className="text-gray-300">|</span>
          <span className="flex items-center gap-1">
            <FaWeightHanging className="text-gray-500" />
            <span className="font-medium">{pet.weight}</span>
          </span>
        </>
      )}
    </div>
  );

  const renderService = (pet, service, index) => {
    const readOnly = !isServiceEditable(pet, service);

    let card;
    if (service.type === "boarding") {
      card = (
        <BoardingServiceCheckinCard
          key={index}
          pet={pet}
          service={service}
          index={index}
          onUpdate={(updatedService) => handleUpdateService(pet.id, index, updatedService)}
          clientId={pet.client_id}
          onReset={() => resetService(pet.id, index)}
          onCancel={() => handleCancelservice(service)}
          onModify={() => handleServiceModify(service, pet)}
          readOnly={readOnly}
        />
      );
    } else if (service.type === "grooming") {
      card = (
        <GroomingServiceCheckinCard
          key={index}
          pet={pet}
          service={service}
          index={index}
          onUpdate={(updatedService) => handleUpdateService(pet.id, index, updatedService)}
          clientId={pet.client_id}
          onCancel={() => handleCancelservice(service)}
          onModify={() => handleServiceModify(service, pet)}
          readOnly={readOnly}
        />
      );
    } else if (service.type === "vet" || service.type === "vet_clinic") {
      card = (
        <VetServiceCheckinCard
          key={index}
          pet={pet}
          service={service}
          index={index}
          onUpdate={(updatedService) => handleUpdateService(pet.id, index, updatedService)}
          clientId={pet.client_id}
          onCancel={() => handleCancelservice(service)}
          onModify={() => handleServiceModify(service, pet)}
          readOnly={readOnly}
        />
      );
    } else {
      card = (
        <div
          key={index}
          className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden mb-4 relative"
        >
          <div className="px-4 py-3 text-sm text-gray-500">
            Unknown service type: {service.type}
          </div>
        </div>
      );
    }

    return <div key={index}>{card}</div>;
  };

  const renderPetContent = (pet) => {
    return (
      <div className="px-5 py-4 space-y-4">
        {renderPetDetails(pet)}

        {/* Tags row */}
        <div className="flex flex-wrap gap-2 mt-2">
          <button
            className={`inline-flex items-center gap-2 text-xs font-semibold px-4 py-1.5 rounded-full transition ${
              activePetPanel[pet.id] === "appointments"
                ? "bg-indigo-100 text-indigo-700"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
            onClick={() => togglePetPanel(pet.id, "appointments")}
          >
            <FaCalendarCheck /> Recent Appointments
            <FaChevronDown
              className={`text-[10px] transition-transform ${
                activePetPanel[pet.id] === "appointments" ? "rotate-180" : ""
              }`}
            />
          </button>
          <button
            className={`inline-flex items-center gap-2 text-xs font-semibold px-4 py-1.5 rounded-full transition ${
              activePetPanel[pet.id] === "medicalCondition"
                ? "bg-indigo-100 text-indigo-700"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
            onClick={() => togglePetPanel(pet.id, "medicalCondition")}
          >
            <FaNotesMedical /> Medical Condition
            <FaChevronDown
              className={`text-[10px] transition-transform ${
                activePetPanel[pet.id] === "medicalCondition" ? "rotate-180" : ""
              }`}
            />
          </button>
          <button
            className={`inline-flex items-center gap-2 text-xs font-semibold px-4 py-1.5 rounded-full transition ${
              activePetPanel[pet.id] === "tag"
                ? "bg-indigo-100 text-indigo-700"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
            onClick={() => togglePetPanel(pet.id, "tag")}
          >
            <FaTag /> Pet Tag
            <FaChevronDown
              className={`text-[10px] transition-transform ${
                activePetPanel[pet.id] === "tag" ? "rotate-180" : ""
              }`}
            />
          </button>
          <button
            className={`inline-flex items-center gap-2 text-xs font-semibold px-4 py-1.5 rounded-full transition ${
              activePetPanel[pet.id] === "photos"
                ? "bg-indigo-100 text-indigo-700"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
            onClick={() => togglePetPanel(pet.id, "photos")}
          >
            <FaCamera /> Upload Photos
            <FaChevronDown
              className={`text-[10px] transition-transform ${
                activePetPanel[pet.id] === "photos" ? "rotate-180" : ""
              }`}
            />
          </button>
          <button
            className={`inline-flex items-center gap-2 text-xs font-semibold px-4 py-1.5 rounded-full transition ${
              activePetPanel[pet.id] === "notes"
                ? "bg-indigo-100 text-indigo-700"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
            onClick={() => togglePetPanel(pet.id, "notes")}
          >
            <FaStickyNote /> Notes
            <FaChevronDown
              className={`text-[10px] transition-transform ${
                activePetPanel[pet.id] === "notes" ? "rotate-180" : ""
              }`}
            />
          </button>
        </div>

        {/* Panels */}
        {activePetPanel[pet.id] && (
          <div className="mt-2 bg-gray-50 rounded-lg p-3 max-h-60 overflow-y-auto">
            {activePetPanel[pet.id] === "appointments" && (
              (() => {
                const petVisits = getPetVisitCards(pet.id);

                if (petVisits.length === 0) {
                  return (
                    <div className="text-sm text-gray-500 text-center py-3">
                      No recent appointments found.
                    </div>
                  );
                }

                return (
                  <div className="space-y-2">
                    {petVisits.map((visit, index) => (
                      <div
                        key={`${pet.id}-${index}`}
                        className="rounded-xl overflow-hidden border border-gray-200"
                      >
                        <button
                          onClick={() =>
                            setExpandedVisit(
                              expandedVisit === `${pet.id}-${index}`
                                ? null
                                : `${pet.id}-${index}`
                            )
                          }
                          className="w-full bg-white border-b border-gray-200 px-2 py-1 flex items-center justify-between"
                        >
                          <div className="flex items-center gap-2 text-xs">
                            <span className="bg-white/20 px-2 py-0.5 rounded-md text-[11px] font-medium">
                              #{visit.orderNumber}
                            </span>
                            <span>
                              {new Date(visit.createdAt).toLocaleDateString("en-GB", {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                              })}
                            </span>
                            <span className="bg-green-300 px-2 py-0.5 rounded-md text-[11px] font-medium">
                              ₹{Math.round(visit.total_price || visit.final_total)}
                            </span>
                            <span className="bg-purple-100 text-purple-700 px-2 py-0.5 rounded-md text-[11px]">
                              {visit.status
                                ?.toLowerCase()
                                .replace(/\b\w/g, (c) => c.toUpperCase())}
                            </span>
                          </div>
                          <FaChevronDown
                            className={`transition ${
                              expandedVisit === `${pet.id}-${index}` ? "rotate-180" : ""
                            }`}
                          />
                        </button>
                        {expandedVisit === `${pet.id}-${index}` && (
                          <div className="bg-white p-3">
                            <div className="grid grid-cols-3 gap-x-6 gap-y-2 text-xs">
                              <div><strong>Pet</strong> : {visit.pet_name}</div>
                              <div>
                                <strong>Category</strong> :
                                {(visit.item_type === "vet_clinic" ? "Vet" : visit.item_type)
                                  ?.replace(/\b\w/g, (char) => char.toUpperCase())}
                              </div>
                              <div>
                                <strong>Date</strong> :{" "}
                                {new Date(visit.start_date).toLocaleDateString("en-GB")}
                              </div>
                              <div><strong>Breed</strong> : {visit.pet_breed}</div>
                              <div><strong>Staff</strong> : {visit.employee_name || "-"}</div>
                              <div><strong>Time</strong> : {visit.start_time || "-"}</div>
                              <div className="col-span-3">
                                <strong>Service</strong> : {visit.service_name}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                );
              })()
            )}
            {activePetPanel[pet.id] === "medicalCondition" && (
              <div className="text-sm text-gray-700">
                {pet.medicalCondition || "None"}
              </div>
            )}
            {activePetPanel[pet.id] === "tag" && (
              <div className="text-sm text-gray-700 flex items-center gap-1">
                <FaTag className="text-gray-500" /> {pet.tag}
              </div>
            )}
            {activePetPanel[pet.id] === "photos" && (
              <PhotoUploader
                value={pet.photos || []}
                onChange={(newPhotos) => handlePetPhotosChange(pet.id, newPhotos)}
                maxFiles={10}
                size="small"
              />
            )}
            {activePetPanel[pet.id] === "notes" && (
              <div className="mt-2">
                <textarea
                  className="w-full border border-gray-300 rounded-lg p-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  rows={4}
                  placeholder="Enter notes for this pet..."
                  value={pet.notes || ""}
                  onChange={(e) => handlePetNotesChange(pet.id, e.target.value)}
                />
              </div>
            )}
          </div>
        )}

        <div className="mt-4">
          {(() => {
            const orderFormItem =
              pet.services.find(
                (service) =>
                  Array.isArray(service.form_ids) && service.form_ids.length > 0
              ) ||
              pet.services.find(
                (service) => service.form_id || service.form_ids?.length > 0
              ) ||
              pet.services[0] ||
              {};

            return (
              <OrderFormsSection
                orderId={order?._id || orderId}
                item={orderFormItem}
                clientPhone={order?.client?.phone || ""}
                onMandatoryStatusChange={() => {}}
                onFormIdsChange={() => {}}
                hideBypassBlock={true}
              />
            );
          })()}
        </div>

        {/* Services */}
        <div>
          <div className="flex items-center gap-2 text-[10px] font-semibold uppercase text-gray-500 mb-2">
            <FaConciergeBell /> Services
          </div>
          <div className="space-y-3">
            {pet.services.map((service, idx) => renderService(pet, service, idx))}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return <LoaderSpiner />;
  }

  if (!order) {
    return null;
  }

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50"
          onClick={onClose}
        />
      )}
      <div
        className={`fixed top-0 right-0 w-full md:w-[90vw] lg:w-[90vw] xl:w-[85vw] 2xl:w-[80vw] h-full bg-white z-50 shadow-2xl transition-transform duration-300 ease-in-out ${
          isOpen ? "translate-x-0" : "translate-x-full"
        } flex flex-col`}
      >
        {/* Check‑in indicator strip */}
        <div className="flex-shrink-0 bg-indigo-600 text-white text-center py-2 font-bold text-sm uppercase tracking-wider flex items-center justify-center gap-2">
          <FaCheckCircle size={14} />
          Check-In
        </div>
        {/* Header */}
        <div className="flex items-start justify-between px-5 py-4 border-b border-gray-200 flex-shrink-0 gap-2">
          <div className="flex flex-col min-w-0 flex-1">
            <span className="font-bold text-lg">
              {order.client.name || order.client.phone || "-"}
            </span>

            {order.client.address && (
              <span className="text-sm text-gray-600 flex items-center gap-1">
                <FaMapPin className="text-gray-400" /> {order.client.address}
              </span>
            )}

            <div className="flex flex-wrap items-center gap-2 mb-2">
              {order.client.name && order.client.phone && (
                <span className="text-sm text-gray-600 flex items-center gap-1">
                  <FaPhone className="text-gray-400" /> {order.client.phone}
                </span>
              )}
              {order.client.email && (
                <span className="text-sm text-gray-600 flex items-center gap-1">
                  <FaEnvelope className="text-gray-400" /> {order.client.email}
                </span>
              )}
            </div>

            <div className="flex flex-wrap gap-2 mb-2">
              <button
                className={`inline-flex items-center gap-2 text-xs font-semibold px-4 py-1.5 rounded-full transition ${
                  activeClientPanel === "overview"
                    ? "bg-indigo-100 text-indigo-700"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
                onClick={() => toggleClientPanel("overview")}
              >
                <FaChartBar /> Overview{" "}
                <FaChevronDown
                  className={`text-[10px] transition-transform ${
                    activeClientPanel === "overview" ? "rotate-180" : ""
                  }`}
                />
              </button>
              <button
                className={`inline-flex items-center gap-2 text-xs font-semibold px-4 py-1.5 rounded-full transition ${
                  activeClientPanel === "recentVisits"
                    ? "bg-indigo-100 text-indigo-700"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
                onClick={() => toggleClientPanel("recentVisits")}
              >
                <FaHistory /> Recent Visits{" "}
                <FaChevronDown
                  className={`text-[10px] transition-transform ${
                    activeClientPanel === "recentVisits" ? "rotate-180" : ""
                  }`}
                />
              </button>
            </div>

            {activeClientPanel && (
              <div className="mt-1 bg-gray-50 rounded-lg p-3 max-h-40 overflow-y-auto">
                {activeClientPanel === "overview" && (
                  <div className="mt-1 bg-gray-50 rounded-lg p-3">
                    {loadingStats ? (
                      <div className="flex justify-center py-4">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
                        <div className="bg-green-50 border border-green-200 p-3 rounded-lg shadow-sm flex flex-col items-center text-center">
                          <FaRupeeSign className="w-4 h-4 text-green-700 mb-1" />
                          <p className="text-gray-600 text-[11px] font-medium">Paid</p>
                          <p className="text-base font-bold text-gray-900 mt-0.5">
                            ₹{Number(calculateStats().paidAmount).toLocaleString("en-IN", {
                              maximumFractionDigits: 0,
                            })}
                          </p>
                        </div>
                        <div className="bg-yellow-50 border border-yellow-200 p-3 rounded-lg shadow-sm flex flex-col items-center text-center">
                          <FaClock className="w-4 h-4 text-yellow-700 mb-1" />
                          <p className="text-gray-600 text-[11px] font-medium">Pending</p>
                          <p className="text-base font-bold text-gray-900 mt-0.5">
                            ₹{Number(calculateStats().pendingAmount).toLocaleString("en-IN", {
                              maximumFractionDigits: 0,
                            })}
                          </p>
                        </div>
                        <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg shadow-sm flex flex-col items-center text-center">
                          <FaReceipt className="w-4 h-4 text-blue-700 mb-1" />
                          <p className="text-gray-600 text-[11px] font-medium">Total Orders</p>
                          <p className="text-base font-bold text-gray-900 mt-0.5">
                            {calculateStats().totalOrders}
                          </p>
                        </div>
                        <div className="bg-red-50 border border-red-200 p-3 rounded-lg shadow-sm flex flex-col items-center text-center">
                          <FaCalendar className="w-4 h-4 text-red-700 mb-1" />
                          <p className="text-gray-600 text-[11px] font-medium">Cancellations</p>
                          <p className="text-base font-bold text-gray-900 mt-0.5">
                            {calculateStats().cancellations}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {activeClientPanel === "recentVisits" && (
                  <div className="space-y-2">
                    {visitCards.map((visit, index) => (
                      <div key={index} className="rounded-xl overflow-hidden border border-gray-200">
                        <button
                          onClick={() =>
                            setExpandedVisit(expandedVisit === index ? null : index)
                          }
                          className="w-full bg-white border-b border-gray-200 px-2 py-1 flex items-center justify-between"
                        >
                          <div className="flex items-center gap-2 text-xs">
                            <span className="bg-white/20 px-2 py-0.5 rounded-md text-[11px] font-medium">
                              #{visit.orderNumber}
                            </span>
                            <span>
                              {new Date(visit.createdAt).toLocaleDateString("en-GB", {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                              })}
                            </span>
                            <span className="bg-green-300 px-2 py-0.5 rounded-md text-[11px] font-medium">
                              ₹{Math.round(visit.total_price || visit.final_total)}
                            </span>
                            <span className="bg-purple-100 text-purple-700 px-2 py-0.5 rounded-md text-[11px]">
                              {visit.status
                                ?.toLowerCase()
                                .replace(/\b\w/g, (char) => char.toUpperCase())}
                            </span>
                          </div>
                          <FaChevronDown
                            className={`transition ${
                              expandedVisit === index ? "rotate-180" : ""
                            }`}
                          />
                        </button>
                        {expandedVisit === index && (
                          <div className="bg-white p-3 space-y-1 text-xs">
                            <div><strong>Pet</strong> : {visit.pet_name}</div>
                            <div><strong>Breed</strong> : {visit.pet_breed}</div>
                            <div><strong>Service</strong> : {visit.service_name}</div>
                            <div>
                              <strong>Category</strong> :
                              {visit.item_type === "vet_clinic"
                                ? " Vet"
                                : ` ${visit.item_type}`}
                            </div>
                            <div><strong>Staff</strong> : {visit.employee_name || "-"}</div>
                            <div>
                              <strong>Date</strong> :{" "}
                              {new Date(visit.start_date).toLocaleDateString("en-GB")}
                            </div>
                            <div><strong>Time</strong> :{visit.start_time || "-"}</div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {activeClientPanel === "notes" && (
                  <div className="text-sm text-gray-700">{order.client.notes}</div>
                )}
              </div>
            )}
          </div>

          <div className="flex flex-col gap-2 flex-1">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-y-2 gap-x-6 text-sm">
              <div>
                <div className="font-medium">#{order.orderNumber}</div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleViewInvoice}
                  disabled={invoiceLoading || hasPricingChanges}
                  className="inline-flex items-center gap-1 text-xs font-semibold text-indigo-600 bg-indigo-50 px-3 py-1.5 rounded-full hover:bg-indigo-100 transition disabled:opacity-50"
                >
                  <FaFileInvoice />
                  {invoiceLoading
                    ? "Loading..."
                    : `Invoice ${order.invoiceNumber || ""}`}
                </button>
              </div>
            </div>

            {showOrderNotes && (
              <div className="mt-1 bg-gray-50 rounded-lg p-3 text-sm text-gray-700 max-h-40 overflow-y-auto">
                {order.orderNotes}
              </div>
            )}

            {/* Payment summary row: Advance | Pending | Total | Pay Now */}
            <div className="mt-2 bg-gray-50 rounded-lg p-3">
              <div className="grid grid-cols-[1.4fr_1.4fr_1fr_auto] gap-4 items-center text-sm">
                <div>
                  <span className="text-gray-400 text-xs whitespace-nowrap">
                    Advance Received
                  </span>
                  <div className="font-medium text-green-600 flex items-center">
                    <FaRupeeSign className="w-3 h-3" />
                    <span>{Number(order.payment.advanceReceived || 0).toFixed(0)}</span>
                  </div>
                </div>
                <div>
                  <span className="text-gray-400 text-xs whitespace-nowrap">
                    Pending Amount
                  </span>
                  <div className="font-medium text-red-600 flex items-center">
                    <FaRupeeSign className="w-3 h-3" />
                    <span>{Number(order.payment.pendingAmount || 0).toFixed(0)}</span>
                  </div>
                </div>
                <div>
                  <span className="text-gray-400 text-xs whitespace-nowrap">Total</span>
                  <div className="font-medium text-blue-700 flex items-center">
                    <FaRupeeSign className="w-3 h-3" />
                    <span>{Number(order.payment.total || 0).toFixed(0)}</span>
                  </div>
                </div>
                {(() => {
                  const pending = Number(order.payment.pendingAmount || 0);
                  const isFullyPaid = pending <= 0;
                  if (isFullyPaid) return null;
                  return (
                    <div className="flex justify-end">
                      <button
                        onClick={() => setShowPaymentDrawer(true)}
                        disabled={hasPricingChanges}
                        className={`text-white text-xs font-medium px-3 py-1.5 rounded-md transition ${
                          hasPricingChanges
                            ? "bg-gray-400 cursor-not-allowed"
                            : "bg-green-600 hover:bg-green-700"
                        }`}
                      >
                        Pay Now
                      </button>
                    </div>
                  );
                })()}
              </div>
            </div>

            {/* ✅ Adjustment card — placed BELOW the payment summary row */}
            <AdjustmentAmountCard
              adjustmentAmount={adjustmentAmount}
              adjustmentReason={adjustmentReason}
              onAdjustmentChange={setAdjustmentAmount}
              onAdjustmentReasonChange={setAdjustmentReason}
              orderTotal={parseFloat(order?.payment?.grandTotal || 0)}
              maxAmount={parseFloat(order?.payment?.grandTotal || 0)}
              showSaveButton
              onSave={handleSaveAdjustment}
              saving={savingAdjustment}
              savedValue={parseFloat(order?.payment?.adjustmentAmount || 0)}
              savedReason={order?.payment?.adjustmentReason || ""}
              disabled={hasPricingChanges || isCancelling}
              className="mt-2"
            />
          </div>

          <button
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition text-gray-500 flex-shrink-0"
          >
            <FaTimes />
          </button>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          <div className="border border-gray-200 rounded-xl bg-white overflow-hidden">
            {order.pets.map((pet) => {
              const isExpanded = expandedPets[pet.id] ?? order.pets.length === 1;
              return (
                <div key={pet.id} className="border-b border-gray-200 last:border-b-0">
                  {renderPetHeader(pet)}
                  {isExpanded && (
                    <div className="bg-white">{renderPetContent(pet)}</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-gray-200 flex justify-end gap-3 flex-shrink-0"></div>
      </div>

      <AddNewPet
        isOpen={showEditPetDrawer}
        onClose={() => {
          setShowEditPetDrawer(false);
          setSelectedPet(null);
        }}
        petId={selectedPet?.id}
        clientId={order?.client?.client_id}
        vendorId={vendorId}
        existingPets={order?.pets || []}
        onSave={() => {}}
        onEditSave={() => {
          setShowEditPetDrawer(false);
          setSelectedPet(null);
          fetchOrder();
          recalculateOrder();
        }}
      />

      <OrderPaymentDrawer
        isOpen={showPaymentDrawer}
        onClose={() => setShowPaymentDrawer(false)}
        orderId={orderId}
        onPaymentSuccess={() => {
          setShowPaymentDrawer(false);
          fetchOrder();
        }}
      />

      {rescheduleDrawer.isOpen && rescheduleDrawer.appointment && (
        <PetServiceCategoryDrawer
          isOpen={rescheduleDrawer.isOpen}
          onClose={() => {
            setRescheduleDrawer({ isOpen: false, appointment: null });
            fetchOrder();
          }}
          onUpdate={() => {
            setRescheduleDrawer({ isOpen: false, appointment: null });
            fetchOrder();
          }}
          clientId={order.client.client_id}
          itemData={{
            ...rescheduleDrawer.appointment,
            ref_id: rescheduleDrawer.appointment?.ref_id,
            vendor_pet_id: rescheduleDrawer.appointment?.vendor_pet_id,
            service_id: rescheduleDrawer.appointment?.service_id,
            service_category_type_id:
              rescheduleDrawer.appointment?.service_category_type_id,
            service_type_name: rescheduleDrawer.appointment?.service_type_name,
            start_date: rescheduleDrawer.appointment?.start_date,
            end_date: rescheduleDrawer.appointment?.end_date,
            item_type: rescheduleDrawer.appointment?.type || "boarding",
            order_id: rescheduleDrawer.appointment?.order_id,
            room_type_id: rescheduleDrawer.appointment?.room_type_id,
            room_id: rescheduleDrawer.appointment?.room_id,
            room_name: rescheduleDrawer.appointment?.room_name,
            room_number: rescheduleDrawer.appointment?.room_number,
            _id:
              rescheduleDrawer.appointment?.id ||
              rescheduleDrawer.appointment?.ref_id,
          }}
          orderId={orderId}
          editMode={true}
          isServiceEditMode={true}
          isPetDetails={false}
          clientName={order.client.name}
          clientNumber={order.client.phone}
        />
      )}

      {showBookingDetails && (
        <BookingDetailsDrawer
          isOpen={showBookingDetails}
          onClose={handleCloseBookingDetails}
          orderId={selectedOrderId}
          mode={"details"}
        />
      )}
    </>
  );
};

export default OrderCheckinDrawer;